import type { RegisteredComponent } from "@builder.io/sdk-react";

import { CLAIM_VARIANTS } from "~/hec/loyalty/insiders-claim.schemas";

/**
 * Builder inputs for `InsidersClaimForm`. Copy fields have NO `defaultValue`
 * on purpose: blank means "use the built-in default for this variant on this
 * site", so the HSA space gets "HSA contribution" wording without a
 * merchandiser edit. Typing anything overrides it.
 */
export const insidersClaimFormInputs = [
  {
    name: "variant",
    friendlyName: "Which claim",
    type: "text",
    enum: [
      { label: "Monthly visit (1x per month)", value: CLAIM_VARIANTS[0] },
      {
        label: "Annual contribution amount (1x per year)",
        value: CLAIM_VARIANTS[1],
      },
      {
        label: "Future plans Yes / No (1x per year)",
        value: CLAIM_VARIANTS[2],
      },
    ],
    required: true,
    defaultValue: CLAIM_VARIANTS[0],
    helperText:
      "Each choice credits a different Perks earn action. Place the block three times for the three cards.",
  },
  {
    name: "theme",
    type: "text",
    enum: [
      { label: "Light card (site tint, dark button)", value: "light" },
      { label: "Dark card (navy, white button)", value: "dark" },
    ],
    defaultValue: "light",
  },
  {
    name: "eyebrow",
    type: "text",
    helperText: 'Empty: "1X PER MONTH" / "1X PER YEAR" for the chosen claim.',
  },
  {
    name: "heading",
    type: "text",
    helperText: 'Empty: "30 Points".',
  },
  {
    name: "body",
    type: "richText",
    helperText:
      "Empty: the live Insiders copy for the chosen claim, with the site's plan code (FSA / HSA).",
  },
  {
    name: "buttonLabel",
    type: "text",
    helperText: 'Empty: "Claim".',
  },
  {
    name: "inputLabel",
    friendlyName: "Amount input label",
    type: "text",
    helperText:
      'Annual contribution only. Empty: "FSA Contribution" / "HSA Contribution" by site.',
    showIf: `options.get('variant') === 'annualContribution'`,
  },
  {
    name: "yesLabel",
    type: "text",
    helperText: 'Future plans only. Empty: "Yes, I plan to enroll".',
    showIf: `options.get('variant') === 'futurePlans'`,
  },
  {
    name: "noLabel",
    type: "text",
    helperText: `Future plans only. Empty: "No, I don't plan to enroll".`,
    showIf: `options.get('variant') === 'futurePlans'`,
  },
  {
    name: "editorState",
    friendlyName: "Editor preview state",
    type: "text",
    enum: [
      { label: "Signed in, ready to claim", value: "ready" },
      { label: "Signed out (sign-in prompt)", value: "guest" },
      { label: "Signed in, not enrolled in Perks", value: "notEnrolled" },
      { label: "After a successful claim", value: "success" },
      { label: "After a rejected claim", value: "error" },
    ],
    defaultValue: "ready",
    advanced: true,
    helperText:
      "Only affects the Visual Editor and previews. Live pages always show the shopper's real state.",
  },
] satisfies NonNullable<RegisteredComponent["inputs"]>;
